import React, { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const AdminDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setDashboard(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();

    // Update time every second
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Polling for updates every 5 seconds
    const pollInterval = setInterval(() => {
      fetchDashboard();
    }, 5000);

    // Try WebSocket connection (fallback to polling if it fails)
    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws/dashboard`;
      const ws = new WebSocket(wsUrl);

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'status_update' || data.type === 'daily_reset') {
          fetchDashboard();
        }
      };

      ws.onerror = () => {
        // WebSocket failed, polling will handle updates
      };

      return () => {
        clearInterval(timeInterval);
        clearInterval(pollInterval);
        ws.close();
      };
    } catch (error) {
      // WebSocket not supported or failed, use polling only
      return () => {
        clearInterval(timeInterval);
        clearInterval(pollInterval);
      };
    }
  }, [fetchDashboard]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleResetSystem = async () => {
    if (!window.confirm('¿Estás seguro de reiniciar el sistema? Todos los mecánicos volverán a estado OCUPADO.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/reset-system`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('✅ Sistema reiniciado exitosamente');
        fetchDashboard();
      }
    } catch (error) {
      console.error('Error resetting system:', error);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-black">
        <div className="text-red-600 text-4xl font-bold animate-pulse">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-black flex flex-col overflow-hidden">
      {/* Header Ultra Compacto */}
      <header className="bg-gradient-to-r from-zinc-900 via-black to-zinc-900 border-b-4 border-red-600 shadow-xl flex-shrink-0">
        <div className="max-w-[2000px] mx-auto px-6 py-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <img 
                src="/serviciomelgoza.png" 
                alt="Servicio Melgoza" 
                className="h-14 w-auto"
                data-testid="dashboard-logo"
              />
              <div className="border-l-4 border-red-600 pl-4">
                <h1 className="text-2xl font-black text-white tracking-tight">TABLERO DE CONTROL</h1>
                <p className="text-xs text-red-500 font-bold">Turnos en Tiempo Real</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-2xl font-bold text-white">
                  {currentTime.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div className="text-xs text-gray-400">
                  {currentTime.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <Button onClick={toggleFullscreen} size="sm" className="h-7 w-7 p-0 bg-zinc-800 hover:bg-zinc-700 text-xs">
                  {isFullscreen ? '❌' : '🖥️'}
                </Button>
                <Button onClick={handleResetSystem} size="sm" className="h-7 w-7 p-0 bg-zinc-800 hover:bg-zinc-700 text-xs">
                  🔄
                </Button>
                <Button onClick={() => navigate('/admin/mechanics')} size="sm" className="h-7 w-7 p-0 bg-zinc-800 hover:bg-zinc-700 text-xs" data-testid="manage-mechanics-button">
                  ⚙️
                </Button>
                <Button onClick={handleLogout} size="sm" className="h-7 px-3 bg-zinc-800 hover:bg-zinc-700 text-xs" data-testid="logout-button">
                  Salir
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Bar Ultra Compacto */}
      <div className="bg-gradient-to-b from-zinc-950 to-black py-2 flex-shrink-0">
        <div className="max-w-[2000px] mx-auto px-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-xl p-3 border-2 border-zinc-700">
              <p className="text-gray-300 text-xs font-bold uppercase mb-1">Total</p>
              <p className="text-white text-4xl font-black" data-testid="total-mechanics">{dashboard?.total || 0}</p>
            </div>
            <div className="bg-gradient-to-br from-green-900/50 to-green-800/30 rounded-xl p-3 border-2 border-green-500">
              <p className="text-green-300 text-xs font-bold uppercase mb-1 flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>Libres
              </p>
              <p className="text-green-400 text-4xl font-black" data-testid="total-free">{dashboard?.total_libres || 0}</p>
            </div>
            <div className="bg-gradient-to-br from-red-900/50 to-red-800/30 rounded-xl p-3 border-2 border-red-500">
              <p className="text-red-300 text-xs font-bold uppercase mb-1 flex items-center">
                <span className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></span>Ocupados
              </p>
              <p className="text-red-400 text-4xl font-black" data-testid="total-busy">{dashboard?.total_ocupados || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Ultra Compacto para caber todo sin scroll */}
      <main className="flex-1 max-w-[2000px] mx-auto px-6 py-2 overflow-hidden">
        <div className="grid grid-cols-2 gap-4 h-full">
          
          {/* Cola de Libres */}
          <div className="flex flex-col h-full">
            <div className="bg-gradient-to-br from-green-900/40 to-green-800/20 border-4 border-green-500 rounded-xl p-3 mb-3 flex-shrink-0">
              <h2 className="text-3xl font-black text-green-300 flex items-center uppercase">
                <svg className="w-8 h-8 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                COLA DE LIBRES
              </h2>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2" data-testid="free-mechanics-list">
              {dashboard?.libres.length === 0 ? (
                <div className="bg-zinc-900 rounded-xl p-6 border-2 border-zinc-800 text-center">
                  <p className="text-gray-400 text-lg">No hay mecánicos libres</p>
                </div>
              ) : (
                dashboard?.libres.map((mech, index) => (
                  <div
                    key={mech.id}
                    className="bg-gradient-to-br from-zinc-900 to-black rounded-xl p-3 border-2 border-green-600 hover:border-green-400 transition-all"
                    data-testid={`free-mechanic-${index}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="bg-gradient-to-br from-green-600 to-green-700 text-white font-black text-3xl w-14 h-14 rounded-xl flex items-center justify-center border-2 border-green-400">
                          #{index + 1}
                        </div>
                        <div>
                          <h3 className="text-white text-2xl font-black">{mech.nombre}</h3>
                          <p className="text-gray-400 text-xs">{mech.email}</p>
                        </div>
                      </div>
                      <div className="bg-green-500 text-black px-4 py-1 rounded-full text-sm font-black border-2 border-green-300">
                        LIBRE
                      </div>
                    </div>

                    {mech.ultimo_terminado_at && (
                      <div className="flex items-center space-x-2">
                        <p className="text-green-400 text-xs font-semibold flex-1">
                          ⏱️ {formatDistanceToNow(new Date(mech.ultimo_terminado_at), { addSuffix: true, locale: es })}
                        </p>
                        {mech.foto_ultimo_terminado_url && (
                          <img
                            src={mech.foto_ultimo_terminado_url}
                            alt="Último vehículo"
                            className="w-16 h-16 object-cover rounded-lg border-2 border-green-600"
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Mecánicos Ocupados */}
          <div className="flex flex-col h-full">
            <div className="bg-gradient-to-br from-red-900/40 to-red-800/20 border-4 border-red-500 rounded-xl p-3 mb-3 flex-shrink-0">
              <h2 className="text-3xl font-black text-red-300 flex items-center uppercase">
                <svg className="w-8 h-8 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                OCUPADOS
              </h2>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2" data-testid="busy-mechanics-list">
              {dashboard?.ocupados.length === 0 ? (
                <div className="bg-zinc-900 rounded-xl p-6 border-2 border-zinc-800 text-center">
                  <p className="text-gray-400 text-lg">Todos libres</p>
                </div>
              ) : (
                dashboard?.ocupados.map((mech, index) => (
                  <div
                    key={mech.id}
                    className="bg-gradient-to-br from-zinc-900 to-black rounded-xl p-3 border-2 border-red-600 hover:border-red-400 transition-all"
                    data-testid={`busy-mechanic-${index}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="text-white text-2xl font-black">{mech.nombre}</h3>
                        <p className="text-gray-400 text-xs">{mech.email}</p>
                      </div>
                      <div className="bg-red-500 text-black px-4 py-1 rounded-full text-sm font-black border-2 border-red-300">
                        OCUPADO
                      </div>
                    </div>

                    {mech.vehiculo_actual && (
                      <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 p-2 rounded-lg border-2 border-red-600">
                        <p className="text-red-400 text-xs font-bold mb-1">🚗 Trabajando en:</p>
                        <p className="text-white text-base font-bold">{mech.vehiculo_actual}</p>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
          
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
